import { BASE_BACKEND_URL } from '../../auth'
import React, { useEffect, useState, useContext } from 'react'

import LoadingScreen from '../../components/LoadingScreen'
import Step1PriceInfo from './components/Step1PriceInfo'
import Step2AssignClients from './components/Step2AssignClients'
import Step3AssignProducts from './components/Step3AssignProducts'
import AlertErrorMessage from './helpers/AlertErrorMessage'
import FormHeaderActions from './components/FormHeaderActions'
import { useFetchClients } from './hooks/useFetchClients'
import { priceListBackend, cancelCreate } from './helpers/priceListHelpers'
import { useLoadPriceList } from './hooks/useLoadPriceList'
import { useNavigate } from 'react-router-dom'

const CreatePriceList = () => {

  const navigate = useNavigate()
  const [failedCreation, setFailedCreation] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [copyPrice, setCopyRoute] = useState(false)
  const [readyToCollect, setReadyToCollect] = useState(false)
  const [saving, setSaving] = useState(false)

  const [title, setTitle] = useState('')
  const [uidPrices, setUidPrices] = useState('')
  const [clients, setClients] = useState([])
  const [products, setProducts] = useState([])

  const {
    initialLoading,
    error,
    isEditing: hookEditing,
    isCopy: hookCopy,
    listInfo
  } = useLoadPriceList(BASE_BACKEND_URL)

  const currentDbRef = listInfo?.uidPrices || null;


  const {
    clientOptions,
    page,
    fetchClients,
    handleSearchChange,
    setClientOptions
  } = useFetchClients(BASE_BACKEND_URL, currentDbRef);

  useEffect(() => {
    if (initialLoading) return

    if (error) {
      setFailedCreation(error)
      return
    }

    setIsEditing(hookEditing)
    setCopyRoute(hookCopy)

    setUidPrices(listInfo.uidPrices)
    setTitle(listInfo.title)
    setClients(listInfo.clients)
    setProducts(listInfo.products)
  }, [initialLoading])

  const handleCloseAlert = (e) => {
    e.preventDefault()
    setFailedCreation(false)
  }

  const handleCancelCreate = (event) => {
  const confirmed = cancelCreate(event)

    if (confirmed) {
      navigate('/listas_de_precios')
    } 
  }
  
  const sendForm = (event) => {
    event.preventDefault()
    setFailedCreation(false)

    const errors = []

    if (!uidPrices.trim()) errors.push('Debe ingresar un código para la lista.')
    if (!title.trim()) errors.push('Debe ingresar un nombre para la lista.')
    if (clients.length === 0) errors.push('Debe asignar al menos un cliente.')
    if (products.length === 0) errors.push('Debe agregar al menos un producto.')

    if (errors.length > 0) {
      setFailedCreation(errors.join(' '))
      return
    }

    setSaving(true)
    setReadyToCollect(true)
  }

  const collectData = () => {
    const jsonData = {
      db_ref: uidPrices,
      description: title,

      mode: isEditing ? "edit" : (copyPrice ? "copy" : "create"),

      clients: clients.map((c) => ({
        uid: c.uid,
        client_name: c.name,
        address: c.address
      })),

      products: products.map((p) => ({
        db_ref: p.db_ref,
        product_name: p.name,
        unit: p.unit,
        base_price: p.base_price,
        price: p.price
      }))
    };

    setSaving(true);
    priceListBackend(BASE_BACKEND_URL, jsonData, evaluateResponse, isEditing, listInfo?.uidPrices);
    setReadyToCollect(false);
  };

  const evaluateResponse = async (response) => {
    try {
      const contentType = response.headers.get('content-type')
      let errorData = {}

      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json()
      }

      switch (response.status) {
        case 201:
          setFailedCreation(false)
          navigate(`/listas_de_precios?success=true`)
          break

        case 200:
          setFailedCreation(false)
          navigate(`/listas_de_precios?updated=true`)
          break

        case 400:
          setFailedCreation('Solicitud inválida. Verifique los datos ingresados.')
          break

        case 404:
          if (errorData?.message === 'Used title') {
            setFailedCreation('El nombre de la lista ya existe. Usa otro nombre.')
          } else if (errorData?.message === 'UID in use') {
            setFailedCreation('El código ya existe en otra lista.')
          } else {
            setFailedCreation('No se encontró el recurso solicitado.')
          }
          break

        case 409:
          if (errorData?.message === 'UID in use') {
            setFailedCreation('El código asociado ya está en uso.')
          } else {
            setFailedCreation('Conflicto detectado. Verifique los datos.')
          }
          break

        case 422:
          if (errorData?.errors?.length > 0) {
            const message = errorData.errors.join(', ')
              .replace('Db ref has already been taken', 'El código ya está en uso.')
              .replace("Description can't be blank", 'La descripción es obligatoria.')
            setFailedCreation(message)
          } else {
            setFailedCreation('Error de validación.')
          }
          break

        case 500:
          setFailedCreation('Error interno del servidor.')
          break

        default:
          setFailedCreation(`Error inesperado (${response.status}).`)
          break
      }
    } catch (err) {
      console.error('Error al procesar la respuesta:', err)
      setFailedCreation('No se pudo conectar con el servidor.')
    } finally {
      setSaving(false)
    }
  }

  const updateSelectedClients = (newSelectedSet) => {
    const updatedClients = Array.from(newSelectedSet).map((value) => {
      const client = clientOptions.find((c) => c.value === value)

      if (!client) {
        console.warn('Cliente no encontrado para', value)
        return { uid: value, name: value, active: false, address: '' }
      }

      return {
        uid: client.value,
        name: client.name,
        active: client.active,
        address: client.address,
        created_at: client.created_at,
      }
    })

    setClients(updatedClients)
  }

  const handleDeleteClient = (uid) => {
    setClients((prev) => prev.filter((c) => c.uid !== uid))
    setClientOptions((prev) =>
      prev.map((opt) =>
        opt.value === uid
          ? { ...opt, price_list: null }
          : opt
      )
    )
  }


  const handleProductsChange = (newProducts) => {
    setProducts(newProducts)
  }

  useEffect(() => {
    if (readyToCollect) collectData()
  }, [readyToCollect])


  const isClientSelected = clients.length > 0

  if (initialLoading) return <LoadingScreen text="Cargando..." />

  return (
    <>
      {failedCreation && (
        <AlertErrorMessage message={failedCreation} onClose={handleCloseAlert} />
      )}

      <div className="show-title">
        {isEditing ? <h2>Editar lista de precio</h2> : <h2>Crear nueva lista de precio</h2>}
      </div>

      <form onSubmit={sendForm}>
        <div className="index-table">
          <div className="show-area">
            <div className="create-promo-wrapper">

              <FormHeaderActions
                onCancel={handleCancelCreate}
                saving={saving}
                copyPrice={copyPrice}
                isEditing={isEditing}
              />

              <hr />

              {saving && (
                <LoadingScreen
                  title="Estamos validando tu lista…"
                  subtitle="Consulta el estado del plan en la columna 'Estado'"
                />
              )}

              {!saving && (
                <>
                  <Step1PriceInfo
                    uidPrices={uidPrices}
                    title={title}
                    setUidPrices={setUidPrices}
                    setTitle={setTitle}
                    isEditing={isEditing}
                    isCopy={copyPrice}
                  />

                  <Step2AssignClients
                    clients={clients}
                    clientOptions={clientOptions}
                    title={title}
                    page={page}
                    fetchClients={fetchClients}
                    handleSearchChange={handleSearchChange}
                    updateSelectedClients={updateSelectedClients}
                    handleDeleteClient={handleDeleteClient}
                  />

                  <Step3AssignProducts
                    baseBackendUrl={BASE_BACKEND_URL}
                    isClientSelected={isClientSelected}
                    title={title}
                    onProductsChange={handleProductsChange}
                    initialProducts={products}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </form>
      </>
  )
}

export default CreatePriceList
